import '../../i18n';
import {useTranslation} from 'react-i18next';
import React from 'react';
import styled from 'styled-components';
import Markdown from 'markdown-to-jsx';
import { EditorContent, useEditor } from '@tiptap/react';
import { Editor as TiptapEditor } from '@tiptap/core';

import customAnalytics from '../../util/custom-analytics';
import {
    compactTiptapMarkdownCss,
    createMarkdownToJsxOptions,
    normalizeMarkdownForStorage,
    prepareMarkdownForTiptap,
    shouldRenderMarkdownAsPlainText
} from '../../util/markdownTheme';
import { createNoteEditorExtensions } from './noteEditorExtensions';

export const NOTE_EDIT_MODES = {
    RICH: 'rich',
    RAW: 'raw'
};

const MARKDOWN_PASTE_HINTS = [
    /^# /m,
    /^\s*[-*+] /m,
    /^\s*\d+\. /m,
    /!\[[^\]]*]\([^)]+\)/,
    /\[[^\]]+]\([^)]+\)/,
    /^Title:\s+/m,
    /^URL Source:\s+/m,
    /^Markdown Content:\s*$/m,
    /^Image\s+\d+\s*:\s*Image\s*$/m
];

const decodeHtmlEntities = value => {
    if (
        typeof value !== 'string' ||
        (!value.includes('&lt;') && !value.includes('&gt;') && !value.includes('&amp;') && !value.includes('&#'))
    ) {
        return value || '';
    }

    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value || value;
};

const looksLikeMarkdownPaste = value => {
    if (typeof value !== 'string') {
        return false;
    }

    const trimmedValue = value.trim();
    if (!trimmedValue || !trimmedValue.includes('\n')) {
        return false;
    }

    return MARKDOWN_PASTE_HINTS.some(pattern => pattern.test(trimmedValue));
};

function RichMarkdownEditor({ values, setFieldValue, onStopEditing, onOpenImagePreview, inline = false }) {
    const {t: lnT} = useTranslation();

    const noteEditorRef = React.useRef(null);
    const editorInstanceRef = React.useRef(null);
    const normalizeBody = React.useCallback(markdown => normalizeMarkdownForStorage(markdown || ''), [lnT]);
    const prepareBody = React.useCallback(markdown => prepareMarkdownForTiptap(markdown || ''), [lnT]);
    const latestBodyRef = React.useRef(normalizeBody(values.body || ''));
    const extensions = React.useMemo(() => createNoteEditorExtensions(), [lnT]);

    const editor = useEditor({
        extensions,
        content: prepareBody(values.body || ''),
        contentType: 'markdown',
        editorProps: {
            attributes: {
                class: 'ln-note-editor'
            },
            handlePaste: (view, event) => {
                const pastedText = decodeHtmlEntities(event.clipboardData?.getData('text/plain') || '');

                if (looksLikeMarkdownPaste(pastedText) && editorInstanceRef.current) {
                    event.preventDefault();
                    const preparedPasteBody = prepareBody(pastedText);

                    try {
                        const parsedPasteEditor = new TiptapEditor({
                            extensions,
                            content: preparedPasteBody,
                            contentType: 'markdown'
                        });
                        const parsedPasteJson = parsedPasteEditor.getJSON();
                        parsedPasteEditor.destroy();

                        const parsedContent = Array.isArray(parsedPasteJson?.content) ? parsedPasteJson.content : [];
                        if (parsedContent.length === 0) {
                            return true;
                        }

                        editorInstanceRef.current.chain().focus().insertContent(parsedContent).run();
                    } catch (error) {
                        editorInstanceRef.current.commands.setContent(preparedPasteBody, {
                            contentType: 'markdown'
                        });
                    }

                    return true;
                }

                if (
                    !pastedText.includes('&lt;') &&
                    !pastedText.includes('&gt;') &&
                    !pastedText.includes('&amp;') &&
                    !pastedText.includes('&#')
                ) {
                    return false;
                }

                event.preventDefault();
                const { state, dispatch } = view;
                dispatch(state.tr.insertText(pastedText, state.selection.from, state.selection.to));
                return true;
            },
            handleKeyDown: (_, event) => {
                if (event.key === 'Escape') {
                    event.preventDefault();
                    onStopEditing();
                    customAnalytics.ui_action({
                        action: 'Note Edit Close',
                        shortcut_key: 'escape',
                        screen: 'MessageScreen'
                    });
                    return true;
                }
                return false;
            }
        },
        onCreate: ({ editor: currentEditor }) => {
            editorInstanceRef.current = currentEditor;
            currentEditor.commands.focus(inline ? 'end' : 'start');
        },
        onDestroy: () => {
            editorInstanceRef.current = null;
        },
        onUpdate: ({ editor: currentEditor }) => {
            const nextMarkdown = typeof currentEditor.getMarkdown === 'function' ? currentEditor.getMarkdown() : '';
            const nextBody = normalizeBody(nextMarkdown);
            if (nextBody !== latestBodyRef.current) {
                latestBodyRef.current = nextBody;
                setFieldValue('body', nextBody, false);
            }
        }
    });

    React.useEffect(() => {
        if (!editor) {
            return;
        }

        const nextBody = normalizeBody(values.body || '');
        if (nextBody === latestBodyRef.current) {
            return;
        }

        latestBodyRef.current = nextBody;
        editor.commands.setContent(prepareBody(nextBody), {
            contentType: 'markdown'
        });
    }, [editor, values.body, normalizeBody, prepareBody]);

    React.useEffect(() => {
        const handleCloseNote = () => {
            onStopEditing();
        };

        const noteEditor = noteEditorRef.current;
        if (noteEditor) {
            noteEditor.addEventListener('closeNoteEditor', handleCloseNote);
        }

        return () => {
            if (noteEditor) {
                noteEditor.removeEventListener('closeNoteEditor', handleCloseNote);
            }
        };
    }, [onStopEditing]);

    return (
        <RichEditorSurface
            $inline={inline}
            onBlur={event => {
                if (!inline) return;
                // Finish once here rather than also submitting through the form's blur handler.
                event.stopPropagation();
                if (!event.currentTarget.contains(event.relatedTarget)) onStopEditing();
            }}
            data-note-editor
            ref={noteEditorRef}
            onClick={event => {
                const clickedImage = event.target instanceof Element ? event.target.closest('img[src]') : null;
                const imageSrc = clickedImage?.getAttribute('src');

                if (imageSrc && typeof onOpenImagePreview === 'function') {
                    event.preventDefault();
                    event.stopPropagation();
                    onOpenImagePreview({
                        src: imageSrc,
                        alt: clickedImage?.getAttribute('alt') || lnT('messages:embeddedImage')
                    });
                    return;
                }

                if (event.target === event.currentTarget && editor) {
                    editor.commands.focus('start');
                }
            }}
        >
            {editor ? <EditorContent editor={editor} /> : null}
            {!values.body ? <RichEditorPlaceholder $inline={inline}>{lnT('messages:writeYourNoteHere')}</RichEditorPlaceholder> : null}
        </RichEditorSurface>
    );
}

function RawMarkdownEditor({ values, setFieldValue, onStopEditing, onOpenImagePreview }) {
    const {t: lnT} = useTranslation();

    const textareaRef = React.useRef(null);
    const sourceText = values.body || '';
    const previewText = normalizeMarkdownForStorage(sourceText);
    const shouldRenderPlainText = shouldRenderMarkdownAsPlainText(previewText);
    const previewMarkdownOptions = React.useMemo(
        () =>
            createMarkdownToJsxOptions({
                surface: 'light',
                onImageClick: onOpenImagePreview
            }),
        [lnT, onOpenImagePreview]
    );

    React.useEffect(() => {
        if (!textareaRef.current) {
            return;
        }

        textareaRef.current.focus();
        const textLength = textareaRef.current.value.length;
        textareaRef.current.setSelectionRange(textLength, textLength);
    }, []);

    return (
        <RawEditorLayout>
            <SourcePane>
                <PaneLabel>{lnT('common:markdown')}</PaneLabel>
                <SourceTextarea
                    ref={textareaRef}
                    value={sourceText}
                    placeholder={lnT('messages:writeYourNoteHere')}
                    onChange={event => {
                        setFieldValue('body', event.target.value, false);
                    }}
                    onKeyDown={event => {
                        if (event.key === 'Escape') {
                            event.preventDefault();
                            onStopEditing();
                            customAnalytics.ui_action({
                                action: 'Note Edit Close',
                                shortcut_key: 'escape',
                                screen: 'MessageScreen'
                            });
                        }
                    }}
                    spellCheck={false}
                />
            </SourcePane>
            <PreviewPane>
                <PaneLabel>{lnT('folders:preview')}</PaneLabel>
                <PreviewBody>
                    {previewText ? (
                        shouldRenderPlainText ? (
                            <PreviewPlainText>{previewText}</PreviewPlainText>
                        ) : (
                            <Markdown options={previewMarkdownOptions}>{previewText}</Markdown>
                        )
                    ) : (
                        <PreviewPlaceholder>{lnT('messages:writeYourNoteHere')}</PreviewPlaceholder>
                    )}
                </PreviewBody>
            </PreviewPane>
        </RawEditorLayout>
    );
}

function NoteEditor({ values, setFieldValue, onStopEditing, mode = NOTE_EDIT_MODES.RICH, onOpenImagePreview, inline = false }) {
    return mode === NOTE_EDIT_MODES.RAW ? (
        <RawMarkdownEditor
            values={values}
            setFieldValue={setFieldValue}
            onStopEditing={onStopEditing}
            onOpenImagePreview={onOpenImagePreview}
        />
    ) : (
        <RichMarkdownEditor
            inline={inline}
            values={values}
            setFieldValue={setFieldValue}
            onStopEditing={onStopEditing}
            onOpenImagePreview={onOpenImagePreview}
        />
    );
}

const RichEditorSurface = styled.div`
    background-color: ${({ $inline }) => $inline ? 'transparent' : 'white'};
    border: 1px solid ${({ $inline }) => $inline ? 'rgba(255, 255, 255, 0.18)' : 'rgba(0, 0, 0, 0.06)'};
    border-radius: 8px;
    padding: 12px;
    min-height: ${({ $inline }) => $inline ? '80px' : '300px'};
    height: ${({ $inline }) => $inline ? 'auto' : '100%'};
    margin-bottom: ${({ $inline }) => $inline ? '8px' : '0'};
    position: relative;
    overflow: auto;
    ${({ $inline }) => compactTiptapMarkdownCss({ surface: $inline ? 'dark' : 'light' })}

    ${({ $inline }) => $inline && `
        & .ln-note-editor { min-height: 56px; }
        & .ln-note-editor, & .ln-note-editor p, & .ln-note-editor li {
            font-size: 14px;
            line-height: 1.4286;
        }
    `}

`;

const RichEditorPlaceholder = styled.div`
    position: absolute;
    left: 12px;
    top: 12px;
    color: ${({ $inline }) => $inline ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)'};
    font-size: 14px;
    pointer-events: none;
`;

const RawEditorLayout = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 10px;
    height: 100%;
    min-height: 0;
    align-items: stretch;

    @media (max-width: 900px) {
        grid-template-columns: minmax(0, 1fr);
        grid-template-rows: minmax(240px, 1fr) minmax(240px, 1fr);
    }
`;

const EditorPaneBase = styled.div`
    min-width: 0;
    min-height: 0;
    border-radius: 10px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`;

const SourcePane = styled(EditorPaneBase)`
    background: white;
    border: 1px solid rgba(0, 0, 0, 0.08);
`;

const PreviewPane = styled(EditorPaneBase)`
    background: rgba(255, 255, 255, 0.74);
    border: 1px solid rgba(255, 255, 255, 0.46);
`;

const PaneLabel = styled.div`
    flex-shrink: 0;
    padding: 9px 12px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.45);
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
`;

const SourceTextarea = styled.textarea`
    flex: 1;
    min-height: 0;
    width: 100%;
    resize: none;
    border: none;
    outline: none;
    padding: 15px 16px;
    background: transparent;
    color: rgba(0, 0, 0, 0.86);
    font-size: 13px;
    line-height: 1.6;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    white-space: pre-wrap;
    word-break: break-word;
    overflow: auto;
    caret-color: rgba(0, 0, 0, 0.86);

    &::placeholder {
        color: rgba(0, 0, 0, 0.35);
    }
`;

const PreviewBody = styled.div`
    flex: 1;
    min-height: 0;
    overflow: auto;
    padding: 15px 16px;
    color: rgba(0, 0, 0, 0.78);
`;

const PreviewPlainText = styled.div`
    white-space: pre-wrap;
    word-break: break-word;
`;

const PreviewPlaceholder = styled.div`
    color: rgba(0, 0, 0, 0.45);
    font-size: 14px;
`;

export default NoteEditor;
